import { countries } from '@/common/countryData'
import { Controller, useFormContext } from 'react-hook-form'
import { OptionHTMLAttributes } from 'react'
import { css } from '@emotion/react'
import { colors, fontSizes, lineHeight, paddings, radii } from '@ohgoodlord/design-system'
import { AlertError, Label, margins, Text } from '@ohgoodlord/design-system'
import { FormValues } from '..'
import { Rules } from '../validation'

const UK_COUNTRY_ID = '583100b7-8302-4ceb-86f9-fea3abb047f7'

const findCountryById = (targetId: string) => countries.find(({ id }) => id === targetId)

const Option = (props: OptionHTMLAttributes<HTMLOptionElement>) => (
  <option {...props} css={optionStyles} />
)

export const NationalityDropdown = ({ rules }: { rules?: Rules<'nationality'> }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>()

  const ukData = findCountryById(UK_COUNTRY_ID)
  const nonUkCountries = countries.filter(({ id }) => id !== UK_COUNTRY_ID)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 5px;
      `}
    >
      <Label htmlFor="nationality">Nationality</Label>
      <Controller
        name="nationality"
        control={control}
        rules={rules}
        render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
          <select
            id="nationality"
            name="nationality"
            required
            ref={ref}
            value={value?.id ?? ''}
            css={selectStyles}
            aria-invalid={!!fieldState.error?.message}
            aria-describedby={fieldState.error ? 'nationality-error' : undefined}
            onBlur={onBlur}
            onChange={e => onChange(findCountryById(e.target.value) ?? null)}
          >
            <Option value="" disabled>
              Select Nationality
            </Option>
            <Option value={ukData?.id}>{ukData?.name}</Option>
            <Option disabled>---</Option>
            {nonUkCountries.map(({ name, id }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </select>
        )}
      />
      {errors.nationality && (
        <div
          id="nationality-error"
          css={css`
            margin-top: ${margins.s};
          `}
        >
          <AlertError>
            <Text>{errors.nationality.message}</Text>
          </AlertError>
        </div>
      )}
    </div>
  )
}

export const selectStyles = css`
  appearance: none;
  font-size: ${fontSizes.m};
  line-height: ${lineHeight};
  width: 100%;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'%3E%3Cpath d='M143 352.3L7 216.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.2 9.4-24.4 9.4-33.8 0z'/%3E%3C/svg%3E")
      no-repeat right ${paddings.xs} center / 0.75em auto,
    linear-gradient(180deg, rgba(242, 242, 242, 0) 0%, #f3f3f3 100%);
  border: 1px solid ${colors.inputBorder};
  border-radius: ${radii.m};
  padding: ${paddings.xs};
  padding-right: calc(${paddings.xs} * 2 + 0.75em);
  color: ${colors.textPrimary};
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &[aria-invalid='true'] {
    border-color: ${colors.red};
  }

  &:hover:not(:disabled) {
    border-color: ${colors.inputBorderHover};
  }

  &:focus {
    outline: none;
    border-color: ${colors.inputBorderFocus};
  }

  &:focus-visible {
    outline: 2px solid #1366d6;
    outline-offset: 5px;
  }
`

export const optionStyles = css`
  font-size: ${fontSizes.m};
  color: ${colors.textPrimary};
  line-height: ${lineHeight};
  padding: ${paddings.xs};
  cursor: pointer;

  &:hover,
  &:focus,
  &:checked {
    background-color: ${colors.inputSelectOptionBackground};
  }

  &:disabled {
    color: ${colors.inputSelectPlaceholder};
  }
`
